
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useToast } from '@/hooks/use-toast'
import { supabase } from '@/integrations/supabase/client'
import { isProduction } from '@/integrations/supabase/client'

export const useCustomAuth = () => {
  const [loading, setLoading] = useState(false)
  const navigate = useNavigate()
  const { toast } = useToast()

  const handleCustomSignUp = async (
    email: string,
    password: string,
    firstName: string,
    lastName: string,
    birthday: string,
    schoolId: string,
    classLevel: string
  ) => {
    try {
      setLoading(true)
      console.log('Starting signup process for:', email)

      const isProd = isProduction()
      
      // For production, webhook handles emails. For staging/dev, use emailRedirectTo
      const signUpOptions: any = {
        data: {
          first_name: firstName,
          last_name: lastName,
          birthday,
          school_id: schoolId,
          class_level: classLevel,
        }
      }

      if (!isProd) {
        const redirectUrl = `${window.location.origin}/auth/confirm`
        signUpOptions.emailRedirectTo = redirectUrl
      }

      // Sign up with Supabase
      const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
        email,
        password,
        options: signUpOptions
      })

      console.log('Signup response:', { 
        user: signUpData?.user?.id,
        session: signUpData?.session ? 'exists' : 'none',
        error: signUpError
      })

      if (signUpError) throw signUpError

      if (!signUpData?.user) {
        throw new Error('No user data returned from signup')
      }

      // Check if school and user combination requires payment
      const { data: schoolData } = await supabase
        .from('schools')
        .select('name')
        .eq('id', schoolId)
        .single()

      const emailDomain = email.split('@')[1]?.toLowerCase() || ''

      // Test bypass: yopmail.com emails using the "Other" school do NOT require payment
      const isTestBypass =
        emailDomain === 'yopmail.com' &&
        schoolData?.name === 'Other'

      // All other new users require payment
      const requiresPayment = !isTestBypass
      
      if (requiresPayment) {
        // Assign unused code and set pending status
        const { data: code } = await supabase
          .from('payment_codes')
          .select('code')
          .eq('is_used', false)
          .limit(1)
          .single()
        
        if (code) {
          await supabase
            .from('profiles')
            .update({ 
              assigned_unlock_code: code.code,
              payment_status: 'pending'
            })
            .eq('id', signUpData.user.id)
          
          toast({
            title: "Account created! 🎉",
            description: "Please complete your payment to access the platform.",
          })
          
          // Redirect to payment info page
          navigate('/auth/payment-info')
        } else {
          // No codes available - log error but allow access
          console.error('No unlock codes available')
          toast({
            title: "Account created! 🎉",
            description: "Your account has been created. Please contact support for payment.",
          })
          navigate('/dashboard')
        }
      } else {
        // Test users (yopmail + Other) get automatic access without payment
        await supabase
          .from('profiles')
          .update({
            payment_status: 'free'
          })
          .eq('id', signUpData.user.id)

        toast({
          title: "Welcome to Dably! 🎉",
          description: "Your account has been created and you can start learning immediately!",
        })
        navigate('/dashboard')
      }

      return true
    } catch (error: any) {
      console.error('Signup error:', error)
      toast({
        title: "Signup failed",
        description: error.message || "Something went wrong. Please try again.",
        variant: "destructive",
      })
      return false
    } finally {
      setLoading(false)
    }
  }

  
  const handleSignIn = async (email: string, password: string) => {
    try {
      setLoading(true)
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      })

      if (error) throw error

      toast({
        title: "Welcome back!",
        description: "You have been successfully signed in.",
      })
      navigate('/dashboard')
      return true
    } catch (error: any) {
      toast({
        title: "Login failed",
        description: error.message || "Invalid email or password",
        variant: "destructive",
      })
      return false
    } finally {
      setLoading(false)
    }
  }

  const handleSignOut = async () => {
    try {
      setLoading(true)
      const { error } = await supabase.auth.signOut()
      if (error) throw error
    } catch (error: any) {
      toast({
        title: "Sign out failed",
        description: error.message,
        variant: "destructive",
      })
    } finally {
      setLoading(false)
    }
  }

  const handlePasswordReset = async (email: string) => {
    try {
      setLoading(true)

      if (isProduction()) {
        // In production, use custom email via webhook for password reset too
        const { error: resetError } = await supabase.auth.resetPasswordForEmail(email)
        if (resetError) throw resetError
      } else {
        // In development/staging, use default Supabase email
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: `${window.location.origin}/auth/reset-password`,
        })
        if (error) throw error
      }

      toast({
        title: "Reset email sent",
        description: "Check your email for the password reset link.",
      })

      return true
    } catch (error: any) {
      toast({
        title: "Password reset failed",
        description: error.message,
        variant: "destructive",
      })
      return false
    } finally {
      setLoading(false)
    }
  }

  const handlePasswordUpdate = async (newPassword: string) => {
    try {
      setLoading(true)
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      })

      if (error) throw error

      toast({
        title: "Password updated",
        description: "Your password has been successfully changed.",
      })

      return true
    } catch (error: any) {
      toast({
        title: "Password update failed",
        description: error.message,
        variant: "destructive",
      })
      return false
    } finally {
      setLoading(false)
    }
  }

  return {
    loading,
    handleCustomSignUp,
    handleSignIn,
    handleSignOut,
    handlePasswordReset,
    handlePasswordUpdate,
  }
}
